import type { FeaturedVideo } from '@/shared/types';
import { getJson, resolveApiUrl } from '@/shared/api/apiClient';

// GET /api/v1/home/showcase — the videos an admin opted in to the public landing
// page (valoi_engine app/routers/home_api.py). The ONLY unauthenticated endpoint
// the app calls: it backs the page a visitor sees before they have an account.
//
// Deliberately not `/home/featured` with the auth dropped. That endpoint returns
// the whole curated feed including `id`, `job_id` and `job_status`, and making it
// public would mean featuring a video also published it. Two switches, two
// surfaces — see the engine's ShowcaseVideoOut docstring.
const SHOWCASE_PATH = '/api/v1/home/showcase';

// A narrower wire shape than FeaturedApiItem: no `id`, no `job_id`, no
// `job_status`, because the public model does not carry them.
type ShowcaseApiItem = {
  title: string;
  description: string;
  video_url: string;
  thumbnail_url?: string | null;
  vtt_url?: string | null;
  runtime_seconds?: number | null;
  tags?: string[] | null;
  category?: string | null;
  style?: string | null;
  published_at: string;
};

/**
 * Map a public entry onto `FeaturedVideo`, the type every browse component is
 * already written against — so the landing page reuses NowShowingHero,
 * ContentRail and BrowseCard unchanged rather than growing parallel copies that
 * drift from the signed-in look.
 *
 * The synthesised `id` is the one wrinkle. `FeaturedVideo.id` is a curation-row
 * id, and the public payload deliberately omits it, but React keys and the hero's
 * `key={video.id}` remount still need something stable per entry. Index-based is
 * correct HERE and nowhere else: this list is read-only, never reordered client
 * -side, and nothing on this page saves, resumes or annotates — the operations
 * that make a real id load-bearing are all behind sign-in.
 */
function toFeaturedVideo(item: ShowcaseApiItem, index: number): FeaturedVideo | null {
  const videoUrl = resolveApiUrl(item.video_url);
  // An entry with no playable URL would render a dead player. The engine already
  // drops these; this is the client half of the same rule.
  if (!videoUrl) return null;
  return {
    id: `showcase-${index}`,
    title: item.title,
    description: item.description,
    videoUrl,
    thumbnailUrl: item.thumbnail_url ? resolveApiUrl(item.thumbnail_url) : undefined,
    vttUrl: item.vtt_url ? resolveApiUrl(item.vtt_url) : undefined,
    durationSeconds: item.runtime_seconds ?? undefined,
    tags: item.tags ?? undefined,
    category: item.category ?? undefined,
    /* How it was rendered. The landing page groups its bands on this, so an entry
       arriving without it lands in no band -- which is why the page falls back to a
       flat list rather than showing nothing when the whole feed lacks it. */
    style: item.style ?? undefined,
    // Read by buildRails to sort "Recently Added" newest-first, the same as the
    // signed-in feed — which is why the public endpoint carries it.
    publishedAt: item.published_at,
  };
}

export async function fetchShowcaseVideos(): Promise<FeaturedVideo[]> {
  const items = await getJson<ShowcaseApiItem[]>(SHOWCASE_PATH, {
    /*
     * `skipAuthRefresh` is load-bearing, not a micro-optimisation. The default
     * path treats any 401 as an expired session: it calls /auth/refresh, and if
     * that still 401s it runs clearAccessToken() and the sign-out handler.
     *
     * This endpoint has no auth, so a 401 from it can only mean the endpoint is
     * misconfigured or missing — never that the viewer's session died. Without
     * this flag, a landing page opened in a tab beside a signed-in one could log
     * that session out on a deploy skew, and every anonymous visit would spend a
     * pointless refresh round-trip first.
     */
    skipAuthRefresh: true,
  });
  return items.map(toFeaturedVideo).filter((video): video is FeaturedVideo => video !== null);
}
